import {
  EditorTheme,
  SaveTemplateVersionBody,
  TemplateCustomCode,
  TemplateVersionSchema,
} from '@loyalty/shared-common/template-api-client';
import { create } from 'zustand';
import { EditorModalContent, EditorService } from '../services/editor-service';
import { defaultEditorTheme } from '../editor/config';

// ============================================================================
// Types
// ============================================================================

interface EditorState {
  // Template state
  currentTemplateId: string | undefined | null;

  // Editor service
  editorService: EditorService | undefined;

  // Theme and custom code
  editorTheme: EditorTheme;
  customCode: TemplateCustomCode | undefined;

  // Selected component
  selectedComponent: any | undefined;

  // Dirty state
  isDirty: boolean;

  // Modal state
  modalContent: EditorModalContent | undefined;
  modalIsOpen: boolean;

  // Locale
  locale: string;
}

interface EditorActions {
  // Template actions
  setCurrentTemplateId: (templateId: string | null) => void;

  // Editor service actions
  initializeEditorService: (editorService: EditorService, locale: string) => void;
  destroyEditorService: () => void;

  // Theme and custom code actions
  setEditorState: (template: {
    schema: TemplateVersionSchema;
    theme: EditorTheme;
    styles?: string;
    customCode?: TemplateCustomCode;
  }) => void;
  setEditorTheme: (theme: EditorTheme) => void;
  setCustomCode: (customCode: TemplateCustomCode) => void;

  // UI actions
  setSelectedComponent: (component: any | undefined) => void;
  unSelectComponent: () => void;

  // JSON actions
  getTemplateJSON: () => SaveTemplateVersionBody;
  setTemplateJSON: (json: SaveTemplateVersionBody) => void;

  // Dirty actions
  markAsDirty: () => void;
  markAsClean: () => void;

  // Modal actions
  openModal: (content: EditorModalContent) => void;
  closeModal: () => void;

  // Locale actions
  setLocale: (locale: string) => void;
}

export type EditorStore = EditorState & EditorActions;

// ============================================================================
// Initial State
// ============================================================================

const initialState: EditorState = {
  currentTemplateId: undefined,
  editorService: undefined,
  editorTheme: defaultEditorTheme,
  customCode: undefined,
  selectedComponent: undefined,
  isDirty: false,
  modalContent: undefined,
  modalIsOpen: false,
  locale: '',
};

// ============================================================================
// Store
// ============================================================================

export const useEditorStore = create<EditorStore>()((setEditorStore, getEditorStore) => ({
  ...initialState,

  // Template actions
  setCurrentTemplateId: templateId => setEditorStore({ currentTemplateId: templateId }),

  // Editor service actions
  initializeEditorService: (editorService, locale) => {
    editorService.editor.I18n.setLocale(locale);
    setEditorStore({ editorService, locale });
  },
  destroyEditorService: () => {
    const { editorService } = getEditorStore();
    editorService?.destroy();
    setEditorStore(initialState);
  },

  // Template actions
  setEditorState: ({ schema, theme, styles, customCode }) => {
    const { editorService } = getEditorStore();
    editorService?.setComponents(schema, styles || '');
    editorService?.setTheme(theme);
    editorService?.setCustomCode(customCode || { css: '' });
    setEditorStore({ editorTheme: theme, customCode });
  },
  setEditorTheme: theme => {
    const { editorService } = getEditorStore();
    editorService?.setTheme(theme);
    setEditorStore({ editorTheme: theme });
  },
  setCustomCode: customCode => {
    const { editorService } = getEditorStore();
    editorService?.setCustomCode(customCode);
    setEditorStore({ customCode });
  },

  // UI actions
  setSelectedComponent: component => setEditorStore({ selectedComponent: component }),

  unSelectComponent: () => {
    const { editorService } = getEditorStore();
    editorService?.editor.select(undefined);
    setEditorStore({ selectedComponent: undefined });
  },

  // JSON actions
  getTemplateJSON: () => {
    const { editorService, customCode, editorTheme } = getEditorStore();
    return {
      schema: {
        components: editorService?.editor.getComponents(),
      },
      styles: editorService?.getStyles(),
      js: editorService?.editor.getJs({}),
      theme: editorTheme,
      customCode,
    };
  },
  setTemplateJSON: (json: SaveTemplateVersionBody) => {
    const { editorService } = getEditorStore();
    editorService?.setComponents(json.schema.components || [], json.styles || '');
    editorService?.setTheme(json.theme);
    editorService?.setCustomCode(json.customCode || { css: '' });
    setEditorStore({ editorTheme: json.theme, customCode: json.customCode });
  },

  // Dirty actions
  markAsDirty: () => setEditorStore({ isDirty: true }),
  markAsClean: () => setEditorStore({ isDirty: false }),

  // Modal actions
  openModal: content => setEditorStore({ modalContent: content, modalIsOpen: true }),
  closeModal: () => setEditorStore({ modalContent: undefined, modalIsOpen: false }),

  // Locale actions
  setLocale: locale => {
    const { editorService } = getEditorStore();
    editorService?.editor.I18n.setLocale(locale);
    setEditorStore({ locale });
  },
}));
